import { graphql } from '@/__generated__';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { getDiscoveryResourceType } from '@/typings/parseUniqueId';
import { useQueryStable } from '../useQueryStable';

type Result = ReturnType<typeof useResourceByEnvironment>[0];

type ResourceResponse = NonNullable<Result['data']>;

export type ResourceAppliedNode = NonNullable<
  ResourceResponse['environment']['applied']
>['resources']['edges'][number]['node'];

export const GetResourceByUniqueId = graphql(/* GraphQL */ `
  query TargetResource(
    $environmentId: BigInt!
    $userId: BigInt
    $resourceType: ResourceNodeType!
    $uniqueId: String!
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        resources(
          filter: { uniqueIds: [$uniqueId], types: [$resourceType] }
          first: 1
        ) {
          edges {
            node {
              uniqueId
              tags
              name
              filePath
              projectId
              ... on SourceAppliedStateNode {
                fqn
                sourceName
                patchPath
                sourceHealthIssues: healthIssues
                freshness {
                  freshnessChecked
                }
                catalog {
                  columns {
                    name
                    type
                    description
                    comment
                    meta
                    descriptionOriginColumnName
                    descriptionOriginResourceUniqueId
                  }
                }
                tests {
                  uniqueId
                  columnName
                  executionInfo {
                    lastRunStatus
                  }
                  testMetadata {
                    name
                    kwargs
                  }
                }
              }
              ... on TestAppliedStateNode {
                fqn
                patchPath
                testMetadata {
                  columnName
                  kwargs
                  name
                  namespace
                }
              }
              ... on ModelAppliedStateNode {
                fqn
                access
                version
                patchPath
                modelHealthIssues: healthIssues
                catalog {
                  columns {
                    name
                    type
                    description
                    comment
                    meta
                    descriptionOriginColumnName
                    descriptionOriginResourceUniqueId
                  }
                }
                tests {
                  uniqueId
                  columnName
                  executionInfo {
                    lastRunStatus
                  }
                  testMetadata {
                    name
                    kwargs
                  }
                }
              }
              ... on SnapshotAppliedStateNode {
                fqn
                patchPath
                catalog {
                  columns {
                    name
                    type
                    description
                    comment
                    meta
                    descriptionOriginColumnName
                    descriptionOriginResourceUniqueId
                  }
                }
                tests {
                  uniqueId
                  columnName
                  executionInfo {
                    lastRunStatus
                  }
                  testMetadata {
                    name
                    kwargs
                  }
                }
              }
              ... on SeedAppliedStateNode {
                fqn
                patchPath
                catalog {
                  columns {
                    name
                    type
                    description
                    comment
                    meta
                    descriptionOriginColumnName
                    descriptionOriginResourceUniqueId
                  }
                }
                tests {
                  uniqueId
                  columnName
                  executionInfo {
                    lastRunStatus
                  }
                  testMetadata {
                    name
                    kwargs
                  }
                }
              }
              ... on ExposureAppliedStateNode {
                fqn
                patchPath
                autoBiProvider
                exposureHealthIssues: healthIssues
              }
              ... on MetricDefinitionNode {
                fqn
                patchPath
              }
              ... on SemanticModelDefinitionNode {
                fqn
                patchPath
              }
              ... on SavedQueryDefinitionNode {
                fqn
              }
            }
          }
        }
      }
    }
  }
`);

export type ResourceByEnvironmentParams = {
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  uniqueId: string | undefined;
  pause?: boolean;
};

export const useResourceByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: ResourceByEnvironmentParams) => {
  const resourceType = getDiscoveryResourceType(uniqueId);
  const query = useQueryStable({
    query: GetResourceByUniqueId,
    variables: {
      environmentId,
      userId: (userId as number) || null,
      uniqueId: uniqueId!,
      resourceType: resourceType!,
    },
    pause:
      pause || !environmentId || !uniqueId || !resourceType || userId === 'loading',
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
